import { sanitizeRef } from './payment-link';
import { rememberSellerSource, type SellerSource } from './checkout-attribution';

/**
 * What brought the buyer to this seller, decided where the buyer meets the
 * seller: their storefront and their product pages. The answer is kept per seller
 * by checkout-attribution.ts and lands on the sale at fulfilment, so a seller can
 * see which sales their own links brought in and which the marketplace found.
 * Owner decision 2026-10-18.
 *
 * THE RULES, in order:
 *  0. A link supa.place made itself (isPlatformLink: our emails' and cards'
 *     review, download and product links): nothing is recorded.
 *  1. The link carries the seller's `?ref=` tag: the seller's own traffic, however
 *     the page was reached. Only the seller hands out tagged links.
 *  2. This page is where the tab started (a full load, not a step inside the
 *     app), so `document.referrer` says where the buyer came from:
 *     - nowhere (an app, an email, a typed or pasted address) or another site:
 *       the seller's own traffic, since that is how a bio link or a post arrives;
 *     - a search engine: search;
 *     - a supa.place discovery page (one opened in a new tab): the marketplace;
 *     - any other supa.place page: unknown, so nothing is recorded.
 *  3. A step inside the app after the buyer has been on a discovery page in this
 *     tab: the marketplace.
 *  4. Anything else: unknown.
 * A weaker answer never replaces a stronger one for the same seller (see
 * SOURCE_STRENGTH), so browsing the marketplace after arriving through the
 * seller's link leaves the sale with the seller's link.
 *
 * ADVISORY, like the payment-link label: a buyer could fake any of it, and the
 * worst that does is mislabel one sale. Nothing that prices, taxes or books a
 * sale reads it.
 */

export type ReferrerKind = 'none' | 'internal' | 'search' | 'external';
export type ClassifiedReferrer =
  | { kind: 'internal'; path: string }
  | { kind: Exclude<ReferrerKind, 'internal'> };

/**
 * Search engines by host. Anchored, so a search company's other services (Gmail
 * at mail.google.com, say) stay "somewhere else": a link in an email is the
 * seller's, not a search result.
 */
const SEARCH_HOSTS: readonly RegExp[] = [
  /^(www\.)?google\.[a-z]{2,3}(\.[a-z]{2})?$/,
  /^(www\.|cn\.)?bing\.com$/,
  /^(www\.|html\.)?duckduckgo\.com$/,
  /^([a-z]{2}\.)?search\.yahoo\.com$/,
  /^search\.yahoo\.co\.jp$/,
  /^(www\.)?yandex\.[a-z]{2,3}$/,
  /^(www\.)?ecosia\.org$/,
  /^search\.brave\.com$/,
  /^(www\.|m\.)?baidu\.com$/,
  /^(www\.)?startpage\.com$/,
  /^(www\.)?qwant\.com$/,
  /^(search\.)?naver\.com$/,
];

/** Where a referrer points: nowhere, this site (with its path), a search engine, or somewhere else. */
export function classifyReferrer(referrer: string, ownHost: string): ClassifiedReferrer {
  if (!referrer) return { kind: 'none' };
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return { kind: 'none' };
  }
  const host = url.hostname.toLowerCase();
  const own = ownHost.toLowerCase().replace(/^www\./, '');
  if (host === own || host === `www.${own}`) return { kind: 'internal', path: url.pathname || '/' };
  if ((url.protocol === 'https:' || url.protocol === 'http:') && SEARCH_HOSTS.some((pattern) => pattern.test(host))) {
    return { kind: 'search' };
  }
  return { kind: 'external' };
}

/**
 * A link supa.place made itself, not the seller: the review and download
 * links in our emails and on cards (`?action=`), and the product links in our
 * receipts and update emails (`?via=email`). A buyer arriving on one is
 * returning to something they bought, so it says nothing about who brought them,
 * and nothing is recorded.
 */
export function isPlatformLink(params: URLSearchParams): boolean {
  return params.has('action') || params.get('via') === 'email';
}

/** The pages where a buyer browses supa.place rather than one seller: the homepage, the marketplace, search, browse and tag pages. */
export function isDiscoveryPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/' || path === '/marketplace' || path === '/search' || path === '/browse' || /^\/tag\/[^/]+$/.test(path);
}

/** The rules above, as a decision. Null when the source is unknown. */
export function sellerSourceFor(input: {
  page: 'storefront' | 'product';
  /** This page is the one the tab loaded, not a step inside the app. */
  landed: boolean;
  referrerKind: ReferrerKind;
  referrerPath?: string;
  /** The URL carries a valid `?ref=` tag. */
  hasRef: boolean;
  /** The buyer has been on a discovery page in this tab. */
  sawDiscovery: boolean;
}): SellerSource | null {
  if (input.hasRef) return input.page;
  if (input.landed) {
    if (input.referrerKind === 'none' || input.referrerKind === 'external') return input.page;
    if (input.referrerKind === 'search') return 'search';
    return input.referrerPath !== undefined && isDiscoveryPath(input.referrerPath) ? 'marketplace' : null;
  }
  return input.sawDiscovery ? 'marketplace' : null;
}

// ---- In the browser -----------------------------------------------------------

/** Set once a discovery page has been shown in this tab's app (SellerSourceTracker). */
let sawDiscoveryPage = false;

/** Note the page the app is showing; a discovery page counts towards rule 3. */
export function noteVisitedPath(pathname: string): void {
  if (isDiscoveryPath(pathname)) sawDiscoveryPage = true;
}

function normalizedPath(path: string): string {
  let decoded = path;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    /* keep it as it came */
  }
  return decoded.replace(/\/+$/, '') || '/';
}

/**
 * Whether the page showing now is the one this document loaded. The navigation
 * timing entry records the URL of the load itself, so a client-side step to
 * another page leaves it naming the first. With no entry to read, the answer is
 * no: an unknown landing is never claimed as the seller's traffic.
 */
function landedHere(): boolean {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (entry && typeof entry.name === 'string' && entry.name) {
      return normalizedPath(new URL(entry.name).pathname) === normalizedPath(window.location.pathname);
    }
  } catch {
    /* fall through */
  }
  return false;
}

/**
 * Record how the buyer reached this seller, from a storefront or product page
 * that knows the seller's id. Best effort: never throws, and does nothing
 * outside a browser.
 */
export function captureSellerSource(params: { sellerId: string; page: 'storefront' | 'product' }): void {
  if (typeof window === 'undefined' || !params.sellerId) return;
  try {
    const search = new URLSearchParams(window.location.search);
    if (isPlatformLink(search)) return;
    const ref = sanitizeRef(search.get('ref'));
    const landed = landedHere();
    const referrer: ClassifiedReferrer = landed
      ? classifyReferrer(document.referrer, window.location.hostname)
      : { kind: 'none' };
    const source = sellerSourceFor({
      page: params.page,
      landed,
      referrerKind: referrer.kind,
      referrerPath: referrer.kind === 'internal' ? referrer.path : undefined,
      hasRef: !!ref,
      sawDiscovery: sawDiscoveryPage,
    });
    if (!source) return;
    const ownLink = source === 'storefront' || source === 'product';
    rememberSellerSource({ sellerId: params.sellerId, source, ...(ref && ownLink ? { ref } : {}) });
  } catch {
    /* advisory: a page that cannot work this out simply records nothing */
  }
}
